import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import SimulationActivity from '../../components/activities/SimulationActivity';
import useSimulationRun from '../../hooks/useSimulationRun';
import useCountdown from '../../hooks/useCountdown';
import tokenStorage from '../../utils/tokenStorage';

/**
 * The live participant screen.
 *
 * Deliberately UNPROTECTED and outside DashboardLayout — participants are anonymous,
 * and DashboardLayout force-redirects anyone unauthenticated to /login. Identity comes
 * from the participant id already stored by the join flow, exactly as the other
 * `/session/:sessionId/...` screens do.
 *
 * All state is the server's. The phone renders `view`, which arrives already redacted.
 */

/**
 * Participant identity via the same accessor every other participant page uses.
 * tokenStorage handles the dual localStorage/sessionStorage layout that survives
 * Safari ITP purges — reading raw keys here would silently miss that fallback.
 */
const readParticipantIdentity = (sessionId) => {
  const data = tokenStorage.getParticipantData(sessionId);
  if (!data?.participantId) return null;
  return { participantId: data.participantId, name: data.name || data.participantName || '' };
};

const SimulationParticipantPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [identity, setIdentity] = useState(null);

  useEffect(() => {
    // A QR link may carry the id directly; otherwise fall back to stored identity.
    const fromUrl = params.get('participantId');
    setIdentity(
      fromUrl
        ? { participantId: fromUrl, name: params.get('name') || '' }
        : readParticipantIdentity(sessionId)
    );
  }, [sessionId, params]);

  const run = useSimulationRun({
    sessionId,
    participantId: identity?.participantId,
    participantName: identity?.name,
  });

  const backToSession = useCallback(
    () => navigate(`/session/${sessionId}/active`, { replace: true }),
    [navigate, sessionId]
  );

  /**
   * When the run ends the phone stays on a "look at the main screen" page: the trainer
   * is about to run the final debrief on the room screen, and that is where attention
   * belongs. The moment the session moves on to anything else, hand the phone back to
   * the normal session screen — otherwise participants would sit on a finished
   * simulation and miss the next activity.
   */
  useEffect(() => {
    if (run.phase === 'closed' && run.sessionMovedOn) backToSession();
  }, [run.phase, run.sessionMovedOn, backToSession]);

  const timed = ['negotiate', 'decide'].includes(run.phase);
  const secondsLeft = useCountdown(timed ? run.phaseEndsAt : null, run.serverNow);

  const sessionCode = useMemo(() => params.get('code') || '', [params]);

  if (!identity) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white px-6">
        <div className="max-w-sm text-center">
          <h1 className="text-lg font-bold text-neutral-900">We don&apos;t know who you are yet</h1>
          <p className="mt-2 text-sm text-neutral-600">
            Join the session first and your trainer will put you on a team.
          </p>
        </div>
      </div>
    );
  }

  if (!run.myFunctionKey || !run.view) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-white px-6">
        <div className="max-w-sm text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-[3px] border-neutral-200 border-t-primary-500" />
          <h1 className="mt-5 text-lg font-bold text-neutral-900">
            {run.isConnected ? 'Waiting for your team' : 'Reconnecting…'}
          </h1>
          <p className="mt-2 text-sm text-neutral-600">
            {run.isConnected
              ? 'Your trainer is assigning teams. This will update on its own.'
              : 'Hold on — we&apos;ll pick up exactly where you were.'}
          </p>
          {run.error && <p className="mt-3 text-xs text-red-600">{run.error}</p>}
        </div>
      </div>
    );
  }

  const iHaveLocked = run.status?.lockedFunctionKeys?.includes(run.myFunctionKey);
  // `locked` is a screen of its own, not a run phase — the round is still in `decide`
  // for everyone else while this team waits.
  const screenPhase = run.phase === 'decide' && (iHaveLocked || run.lockedCardId) ? 'locked' : run.phase;

  return (
    <>
      {run.reconnecting && (
        <div className="fixed inset-x-0 top-0 z-[60] bg-amber-500 py-1.5 text-center text-xs font-semibold text-white">
          Reconnecting…
        </div>
      )}
      <SimulationActivity
        view={run.view}
        roundIndex={run.roundIndex}
        phase={screenPhase}
        myFunctionKey={run.myFunctionKey}
        selectedCardId={run.draftCardId}
        lockedCardId={run.lockedCardId}
        lockedCount={run.status?.lockedCount || 0}
        reveal={run.view?.reveal}
        sessionCode={sessionCode}
        timeRemaining={typeof secondsLeft === 'number' ? secondsLeft : undefined}
        onSelect={run.selectCard}
        onLockIn={run.lockIn}
        onBackToSession={backToSession}
      />
    </>
  );
};

export default SimulationParticipantPage;
